import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Lock, Play } from "@phosphor-icons/react";
import { LogoQuilla } from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const [license, setLicense] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(license, pin);
      toast.success(`Bienvenido, ${data.user.name}`);
      const r = data.user.role;
      if (r === "quilla_admin") nav("/consola");
      else if (r === "housekeeping") nav("/housekeeping");
      else if (r === "mantenimiento") nav("/mantenimiento");
      else if (r === "compras") nav("/compras");
      else if (r === "gerencia") nav("/gerencia");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  const quick = (l, p) => { setLicense(l); setPin(p); };

  return (
    <div className="min-h-screen grid md:grid-cols-2 q-safe-top q-safe-bottom">
      <div className="hidden md:block relative q-noise" style={{
        backgroundImage: "linear-gradient(135deg, rgba(19,35,64,0.9), rgba(31,56,100,0.8)), url('https://images.pexels.com/photos/5288134/pexels-photo-5288134.jpeg')",
        backgroundSize: "cover", backgroundPosition: "center"
      }}>
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <LogoQuilla variant="blanco" size={44} />
          <div>
            <h1 className="text-5xl font-light leading-none mb-3">
              El pulso operativo <br/>de tu hotel.
            </h1>
            <p className="text-white/70 max-w-md">
              Reporte, aprobación y ejecución del mantenimiento hotelero — con Health Index en vivo y el cerebro de QUILLA IA guiando cada decisión.
            </p>
          </div>
          <div className="q-label text-white/50">v1 · fórmula Health Index congelada</div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 md:px-16 py-10">
        <div className="max-w-md w-full mx-auto md:mx-0">
          <div className="md:hidden mb-8"><LogoQuilla variant="color" size={40} /></div>

          <div className="q-label text-[color:var(--q-gold-dark)] mb-2">Acceso a tu licencia</div>
          <h2 className="text-3xl mb-6">Ingresa con Licencia y PIN</h2>

          <button
            type="button"
            onClick={() => nav("/demo")}
            data-testid="watch-demo-btn"
            className="w-full mb-6 h-12 px-4 rounded-lg border-2 border-[color:var(--q-gold)] bg-[color:var(--q-gold)]/10 hover:bg-[color:var(--q-gold)]/20 flex items-center justify-center gap-2 text-sm font-semibold text-[color:var(--q-navy)] transition-all group"
          >
            <div className="h-7 w-7 rounded-full bg-[color:var(--q-gold)] flex items-center justify-center group-hover:scale-110 transition-transform">
              <Play size={12} weight="fill" className="text-white ml-0.5"/>
            </div>
            <span>Ver demo guiado</span>
            <span className="text-xs text-[color:var(--q-muted)] font-normal">60 segundos · sin registro</span>
          </button>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="q-label block mb-2">Licencia</label>
              <input data-testid="login-license-input" value={license} onChange={(e) => setLicense(e.target.value)} placeholder="QF-HOTEL-XX"
                className="w-full h-12 px-4 rounded-lg border border-[color:var(--q-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--q-navy)] font-mono uppercase" autoCapitalize="characters"/>
            </div>
            <div>
              <label className="q-label block mb-2 flex items-center gap-1"><Lock size={12}/> PIN</label>
              <input data-testid="login-pin-input" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)}
                placeholder="••••" maxLength={6}
                className="w-full h-12 px-4 rounded-lg border border-[color:var(--q-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--q-navy)] font-mono tracking-[0.5em]"/>
            </div>
            <button data-testid="login-submit-btn" disabled={loading || !license || !pin} className="q-btn-primary w-full h-12">
              {loading ? "Ingresando..." : "Entrar"}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[color:var(--q-border)]">
            <div className="q-label mb-3 text-[color:var(--q-muted)]">Cuentas de demostración</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button data-testid="quick-maria" onClick={() => quick("QF-ELFARO-26", "1122")} className="q-btn-ghost text-left">
                <div className="font-semibold">María · Housekeeping</div>
                <div className="text-[color:var(--q-muted)]">Ocean El Faro</div>
              </button>
              <button data-testid="quick-jose" onClick={() => quick("QF-ELFARO-26", "1357")} className="q-btn-ghost text-left">
                <div className="font-semibold">José · Mantenimiento</div>
                <div className="text-[color:var(--q-muted)]">Ocean El Faro</div>
              </button>
              <button data-testid="quick-pedro" onClick={() => quick("QF-ELFARO-26", "3690")} className="q-btn-ghost text-left">
                <div className="font-semibold">Pedro · Compras</div>
                <div className="text-[color:var(--q-muted)]">Ocean El Faro</div>
              </button>
              <button data-testid="quick-gerencia" onClick={() => quick("QF-ELFARO-26", "2468")} className="q-btn-ghost text-left">
                <div className="font-semibold">Gerencia</div>
                <div className="text-[color:var(--q-muted)]">Ocean El Faro</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
